import React, { useState, useEffect } from "react";
import UserScreenWindow from "../User/Components/UserScreenWindow";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getAllDocsSnap, addNewDoc } from "../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { v4 } from "uuid";
import IncidentReportDetails from "../Dashboard/Security/IncidentReportDetails";
import {
  FiAlertTriangle,
  FiClipboard,
  FiCheckCircle,
  FiClock,
  FiSearch,
  FiX,
  FiPlus,
  FiHome,
  FiCalendar,
  FiUser,
  FiMapPin,
  FiFileText,
  FiInfo,
  FiLayers,
  FiShield,
  FiKey,
  FiTruck,
  FiTool,
  FiUserX,
  FiEye,
  FiAlertCircle,
  FiBell,
  FiHelpCircle,
  FiAlertOctagon,
  FiExternalLink,
} from "react-icons/fi";

// Incident type icons mapping
const getIncidentIcon = (type) => {
  switch (type) {
    case "Theft":
      return <FiKey className="w-3 h-3 flex-shrink-0" />;
    case "Robbery":
      return <FiShield className="w-3 h-3 flex-shrink-0" />;
    case "Traffic Accident":
      return <FiTruck className="w-3 h-3 flex-shrink-0" />;
    case "Vandalism":
      return <FiTool className="w-3 h-3 flex-shrink-0" />;
    case "Assault":
      return <FiUserX className="w-3 h-3 flex-shrink-0" />;
    case "Suspicious Activity":
      return <FiEye className="w-3 h-3 flex-shrink-0" />;
    case "Homicide/Murder":
      return <FiAlertOctagon className="w-3 h-3 flex-shrink-0" />;
    case "Fire":
      return <FiAlertCircle className="w-3 h-3 flex-shrink-0" />;
    case "Alarm & Scandal":
      return <FiBell className="w-3 h-3 flex-shrink-0" />;
    case "Others":
      return <FiHelpCircle className="w-3 h-3 flex-shrink-0" />;
    default:
      return <FiInfo className="w-3 h-3 flex-shrink-0" />;
  }
};

// Incident Report Card Component
const IncidentReportCard = ({ report, formatDate, onViewDetails }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md">
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <div className="text-sm font-medium text-[#363062]">
            {report.incidentType || "Unnamed Incident"}
          </div>
          <div
            className="flex items-center px-2 py-1 rounded-full text-xs font-medium gap-1.5"
            style={{
              backgroundColor:
                report.status === "pending" ? "#8FCD0015" : "#4299E115",
              color: report.status === "pending" ? "#8FCD00" : "#4299E1",
            }}
          >
            <span className="text-xs">
              {report.status === "pending" ? <FiClock /> : <FiCheckCircle />}
            </span>
            {report.status === "pending" ? "Pending" : "Resolved"}
          </div>
        </div>

        {report.location && (
          <div className="mb-2 flex items-center gap-1.5 text-xs text-gray-600">
            <FiMapPin className="text-gray-400 flex-shrink-0" />
            <span>{report.location}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
          <div className="flex items-center gap-1.5">
            <FiCalendar className="text-gray-400 flex-shrink-0" />
            <span>
              {report.date || formatDate(report.createdAt).split(",")[0]}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <FiClock className="text-gray-400 flex-shrink-0" />
            <span>
              {report.time || formatDate(report.createdAt).split(",")[1]}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <FiUser className="text-gray-400 flex-shrink-0" />
            <span>{report.createdName || "Anonymous"}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <FiFileText className="text-gray-400 flex-shrink-0" />
            <span>Police: {report.policeReported}</span>
          </div>
        </div>

        {report.description && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-600 line-clamp-2">
              {report.description}
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="py-2 px-4 bg-gray-50 border-t border-gray-100">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">
            Reported: {formatDate(report.createdAt)}
          </span>
          <button
            className="px-3 py-1.5 text-xs bg-[#363062] text-white rounded-md hover:bg-[#363062]/80 transition flex items-center gap-1.5"
            onClick={() => onViewDetails(report)}
          >
            <FiExternalLink className="w-3 h-3" />
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};

// Incident Form Component
const IncidentFormMobile = ({
  onClose,
  onSubmit,
  currentUser,
  incidentId,
  isProcessing,
}) => {
  const [activeLink, setActiveLink] = useState("No");
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().slice(0, 5),
  });
  const [selectedIncident, setSelectedIncident] = useState("");
  const [editing, setEditing] = useState(false);

  const links = ["Yes", "No"];

  const incidentTypes = [
    "Theft",
    "Robbery",
    "Traffic Accident",
    "Vandalism",
    "Assault",
    "Suspicious Activity",
    "Homicide/Murder",
    "Fire",
    "Alarm & Scandal",
    "Others",
  ];

  const handleChange = (e) => {
    setEditing(true);
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedIncident) {
      toast.error("Please select an incident type");
      return;
    }

    if (!formData.location) {
      toast.error("Please enter the incident location");
      return;
    }

    if (!formData.description) {
      toast.error("Please provide a description of the incident");
      return;
    }

    const docObject = {
      ...formData,
      policeReported: activeLink,
      incidentType: selectedIncident,
      createdBy: currentUser.uid,
      createdName: currentUser.displayName,
      createdEmail: currentUser.email,
      updateType: "Incident Report Created",
      createdAt: new Date().toLocaleString(),
      status: "pending",
      history: [
        {
          action: "Created incident report",
          at: new Date().toLocaleString(),
          by: currentUser.displayName,
        },
      ],
    };

    onSubmit(docObject);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="fixed inset-0 bg-white z-20 overflow-y-auto pb-20"
    >
      <div className="px-4 py-3 bg-[#363062] text-white flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FiAlertTriangle className="text-lg" />
          <h2 className="text-sm font-medium">Report Incident</h2>
        </div>
        <button onClick={onClose} className="p-1 rounded-full bg-white/20">
          <FiX className="text-white text-lg" />
        </button>
      </div>

      <div className="p-4">
        <div className="mb-5 bg-[#363062]/5 p-3 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FiAlertTriangle className="text-white text-sm" />
            </div>
            <div>
              <h1 className="text-xs font-medium text-[#363062]">
                New Incident Report
              </h1>
              <p className="text-xs text-gray-500">Ref: {incidentId}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Incident Type */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
            <h3 className="text-xs font-semibold mb-3 text-[#363062] flex items-center gap-2">
              <FiInfo className="w-3.5 h-3.5" />
              Incident Type
            </h3>

            <div className="grid grid-cols-2 gap-2">
              {incidentTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`p-2 rounded-lg text-xs font-medium text-left flex items-center gap-2 ${
                    selectedIncident === type
                      ? "bg-[#363062] text-white"
                      : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => {
                    setSelectedIncident(type);
                    setEditing(true);
                  }}
                >
                  {getIncidentIcon(type)}
                  <span className="truncate">{type}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Incident Details */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
            <h3 className="text-xs font-semibold mb-3 text-[#363062] flex items-center gap-2">
              <FiLayers className="w-3.5 h-3.5" />
              Incident Details
            </h3>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  Location
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FiMapPin className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="location"
                    placeholder="Enter location of incident"
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062]"
                    onChange={handleChange}
                    value={formData.location || ""}
                    disabled={isProcessing}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">
                    Date
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <FiCalendar className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      name="date"
                      className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062]"
                      onChange={handleChange}
                      value={formData.date || ""}
                      disabled={isProcessing}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">
                    Time
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <FiClock className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="time"
                      name="time"
                      className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062]"
                      onChange={handleChange}
                      value={formData.time || ""}
                      disabled={isProcessing}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  Police Reported?
                </label>
                <div className="flex items-center gap-2">
                  {links.map((link, index) => (
                    <button
                      type="button"
                      className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                        activeLink === link
                          ? "bg-[#363062] text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                      key={index}
                      onClick={() => {
                        setActiveLink(link);
                        setEditing(true);
                      }}
                    >
                      {link}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  Description
                </label>
                <textarea
                  className="w-full p-3 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062] resize-none"
                  rows={8}
                  name="description"
                  placeholder="Provide a detailed description of the incident..."
                  onChange={handleChange}
                  value={formData.description || ""}
                  disabled={isProcessing}
                ></textarea>
              </div>
            </div>
          </div>

          {/* Reporter Information */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
            <h3 className="text-xs font-semibold mb-3 text-[#363062] flex items-center gap-2">
              <FiUser className="w-3.5 h-3.5" />
              Reporter Information
            </h3>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  Reported By
                </label>
                <div className="text-xs font-medium">
                  {currentUser.displayName || "N/A"}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  Email
                </label>
                <div className="text-xs font-medium">
                  {currentUser.email || "N/A"}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Date</label>
                <div className="text-xs font-medium">
                  {new Date().toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="space-y-3 pt-2">
            <button
              type="submit"
              disabled={isProcessing}
              className="w-full py-2.5 bg-[#363062] text-white rounded-md text-xs font-medium flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FiCheckCircle className="w-3.5 h-3.5" />
                  Submit Incident Report
                </>
              )}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="w-full py-2.5 border border-gray-200 rounded-md text-xs text-gray-600"
              disabled={isProcessing}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

function SecurityIncidents() {
  const currentUser = useSelector((state) => state.userObject);
  const [showForm, setShowForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [incidentId] = useState(v4().slice(0, 8).toUpperCase());
  const [viewDetails, setViewDetails] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // Load reports on component mount
  useEffect(() => {
    const fetchReports = async () => {
      await getAllDocsSnap("incidentReports", (data) => {
        setReports(data);
        setIsLoading(false);
      });
    };
    fetchReports();
  }, []);

  // Filtered reports based on search and filter
  const filteredReports = reports
    .filter((report) => {
      if (filter === "all") return true;
      return report.status === filter;
    })
    .filter((report) => {
      if (!searchTerm.trim()) return true;

      const query = searchTerm.toLowerCase().trim();
      return (
        report.incidentType?.toLowerCase().includes(query) ||
        report.location?.toLowerCase().includes(query) ||
        report.description?.toLowerCase().includes(query)
      );

      
    });

  // Calculate statistics
  const stats = {
    totalIncidents: reports.length,
    resolvedIncidents: reports.filter((report) => report.status === "resolved")
      .length,
    pendingIncidents: reports.filter((report) => report.status === "pending")
      .length,
    recentIncidents: reports.filter((report) => {
      const reportDate = new Date(report.createdAt);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return reportDate >= sevenDaysAgo;
    }).length,
  };

  const handleSubmitIncident = async (docObject) => {
    setIsProcessing(true);

    try {
      await addNewDoc("incidentReports", docObject);
      toast.success("Incident Report Submitted Successfully");
      setShowForm(false);
    } catch (error) {
      toast.error("Error submitting report: " + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleViewDetails = (report) => {
    setSelectedReport(report);
    setViewDetails(true);
  };

  const handleCloseDetails = () => {
    setViewDetails(false);
    setSelectedReport(null);
  };

  return (
    <UserScreenWindow label="Security Incidents">
      {viewDetails && selectedReport ? (
        <IncidentReportDetails
          data={selectedReport}
          setViewMode={handleCloseDetails}
        />
      ) : (
        <div className="min-h-screen bg-gray-50 p-4 pb-20">
          {/* Header Section */}
          <div className="flex items-center justify-between mb-5 bg-[#363062]/5 p-3 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-[#363062] rounded-lg">
                <FiAlertTriangle className="text-white text-sm" />
              </div>
              <div>
                <h1 className="text-xs font-medium text-[#363062]">
                  Security Incidents
                </h1>
                <p className="text-xs text-gray-500">
                  Track and report security incidents
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center px-3 py-2 text-xs font-medium text-white rounded-md bg-[#8FCD00] shadow-sm hover:bg-[#7db700] transition-colors"
            >
              <FiPlus className="w-3 h-3 mr-1.5" />
              New
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gray-50 rounded-lg">
                  <FiClipboard className="w-3.5 h-3.5 text-gray-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Total</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {stats.totalIncidents}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-green-50 rounded-lg">
                  <FiCheckCircle className="w-3.5 h-3.5 text-green-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Resolved</div>
                  <div className="text-sm font-semibold text-green-600">
                    {stats.resolvedIncidents}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-orange-50 rounded-lg">
                  <FiClock className="w-3.5 h-3.5 text-orange-500" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Pending</div>
                  <div className="text-sm font-semibold text-orange-500">
                    {stats.pendingIncidents}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-red-50 rounded-lg">
                  <FiAlertTriangle className="w-3.5 h-3.5 text-red-500" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Recent</div>
                  <div className="text-sm font-semibold text-red-500">
                    {stats.recentIncidents}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <FiSearch className="w-3.5 h-3.5 text-gray-500" />
            </div>
            <input
              type="text"
              className="block w-full p-2.5 pl-10 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
              placeholder="Search incidents by type, location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ borderColor: "#E2E8F0" }}
            />
            {searchTerm && (
              <button
                className="absolute inset-y-0 right-0 flex items-center pr-3"
                onClick={() => setSearchTerm("")}
              >
                <FiX className="w-3.5 h-3.5 text-gray-500" />
              </button>
            )}
          </div>

          {/* Status Filter Buttons */}
          <div className="mb-4 overflow-x-auto">
            <div className="flex space-x-2 pb-2">
              <button
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  filter === "all"
                    ? "bg-[#363062] text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                onClick={() => setFilter("all")}
              >
                All ({reports.length})
              </button>
              <button
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  filter === "pending"
                    ? "bg-[#8FCD00] text-white"
                    : "bg-[#8FCD00]/10 text-gray-700 hover:bg-[#8FCD00]/20"
                }`}
                onClick={() => setFilter("pending")}
              >
                Pending ({stats.pendingIncidents})
              </button>
              <button
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  filter === "resolved"
                    ? "bg-[#4299E1] text-white"
                    : "bg-[#4299E1]/10 text-gray-700 hover:bg-[#4299E1]/20"
                }`}
                onClick={() => setFilter("resolved")}
              >
                Resolved ({stats.resolvedIncidents})
              </button>
            </div>
          </div>

          {/* Incident Reports */}
          <div className="space-y-3">
            {isLoading ? (
              <div className="flex justify-center items-center h-40">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#363062]"></div>
                  <p className="mt-2 text-xs text-[#363062]">
                    Loading reports...
                  </p>
                </div>
              </div>
            ) : filteredReports.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
                <div className="p-3 bg-[#363062]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  <FiAlertTriangle className="text-[#363062] text-lg" />
                </div>
                <h3 className="text-sm font-medium text-[#363062] mb-1">
                  {reports.length === 0
                    ? "No Incidents"
                    : "No Matching Results"}
                </h3>
                <p className="text-xs text-gray-500">
                  {reports.length === 0
                    ? "No incidents have been reported yet"
                    : "Try adjusting your filters or search terms"}
                </p>
              </div>
            ) : (
              filteredReports.map((report) => (
                <IncidentReportCard
                  key={report.id}
                  report={report}
                  formatDate={formatDate}
                  onViewDetails={handleViewDetails}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <IncidentFormMobile
          onClose={() => setShowForm(false)}
          onSubmit={handleSubmitIncident}
          currentUser={currentUser}
          incidentId={incidentId}
          isProcessing={isProcessing}
        />
      )}
    </UserScreenWindow>
  );
}

export default SecurityIncidents;
